// @ts-nocheck

import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import Alert from '@shared/ui/alert/ui/Alert';
import { dismissAlert } from '@shared/store/slices/alert.actions';
import Timer from '@shared/ui/timer/ui/Timer';
import LineSeparator from '@shared/ui/line-separator/ui/LineSeparator';
import RoundLoader from '@shared/ui/round-loader/ui/RoundLoader';
import DynamicScrollToTop from '@shared/ui/dynamic-scroll-to-top/ui/DynamicScrollToTop';
import { InputErrorMessage } from '@shared/ui/input-error-message/ui/InputErrorMessage';
import { OperatorsMessage } from '@shared/ui/operators-message/ui/OperatorsMessage';
import { AfterClickingSubscribe } from '@shared/ui/after-clicking-subscribe/ui/AfterClickingSubscribe';
import { MainLayoutContainer } from '@shared/ui/main-layout-container';
import { BannerTop } from '@shared/ui/banner-top';
import { Footer } from '@shared/ui/footer';

import { GoBack } from '@shared/assets/svgs/GoBack';
import Icons from '@shared/config/constants/Icons';
import { isAE, isBH, isIQ, isLY, isQA } from '@entities/country/model/utils';
import { isBHZainSLA, isKSAVirgin, isMoroccoDvContext } from '@entities/operator';
import { isKSARedbull } from '@entities/operator/model/utils';
import { useLocationHasParticularOperator } from '@entities/operator/model/hooks/useLocationHasParticularOperator';
import { useCheckUniqueApiSource } from '@shared/hooks/useCheckUniqueApiSource';
import { useWideLayout } from '@shared/hooks';
import { URLS } from '@shared/config/config';
import { WEB_ETISALAT_IMMERSIVE } from '@shared/config/constants/api-sources';
import { otpBanners } from '@shared/config/constants/Banners';
import { TEST_IDS } from '@shared/config/constants/test-ids';
import { SubscribtionContext } from '@shared/store/context/SubscribtionContext';
import { useConfirmationPrice } from '@features/phone-subscription/model/hooks/useConfirmationPrice';

import { useOTPLogic } from '@features/otp-verification/model/useOTPLogic';
import { PinFormOperatorText } from '@features/otp-verification/ui/PinFormOperatorText';

import styles from '@features/otp-verification/styles/PinFormStyles.module.css';

const PinForm = () => {
  const { isTVLayout } = useWideLayout();
  const { t } = useTranslation();
  const history = useHistory();

  const {
    pin,
    invalidLength,
    errors,
    antifraudUniqueId,
    inputId,
    language,
    homeData,
    subscribeData,
    operator,
    source,
    btnLoading,
    otpError,
    operatorSelectedDetails,
    isV3Route,
    alerts,
    urlConfig,
    MAHESecondClickId,
    otpDigitsCount,
    minPinLength,
    maxPinLength,
    handlePinChange,
    handleSubmit,
    handleExitPage,
  } = useOTPLogic({ variant: 'standard' });

  const isMoroccoDv = isMoroccoDvContext(
    source,
    operator?.value,
    homeData?.source,
    subscribeData?.operatorName,
    homeData?.operators?.[0]?.value
  );
  const invalidPinText = t(isMoroccoDv ? 'dv_invalid_pin' : 'invalid_pin');
  const invalidPinV3Text = t(isMoroccoDv ? 'dv_invalid_pin_v3' : 'invalid_pin_v3');
  const uniqueApiSource = useCheckUniqueApiSource();
  const { phone } = useContext(SubscribtionContext);
  const confirmationPrice = useConfirmationPrice();

  const operatorObject =
    homeData?.operators?.find((o) => o?.value === subscribeData?.operatorName) || {};
  const operatorCustomTerms = operatorObject?.otpTermsText || null;

  const {
    buttonConfigs: urlConfigButtonConfigs = {},
    termsAndConditions: urlConfigTermsAndConditions = {},
    OTPTermsAndConditions: urlConfigOperatorTermsAndConditions = {},
    otpBannerConfig: urlConfigOtpBannerConfig = {},
    textAboveOTPBanner: urlConfigTextAboveOTPBanner = {},
    textUnderOTPBanner: urlConfigTextUnderOTPBanner = {},
    textUnderOTPButton: urlConfigtextUnderOTPButton = {},
    displayExitButton = false,
    textOnOTPBanner: urlConfigTextOnOTPBanner = {},
  } = Object.values(urlConfig)?.[0] || {};

  const { dcb: buttonConfigsDCB } = urlConfigButtonConfigs;
  const { dcb: termsAndConditionsDCB } = urlConfigTermsAndConditions;
  const { OTPButton: otpButtonText, OTPButtonOffer: otpButtonOfferText } = buttonConfigsDCB || {};

  const isKSAZain = useLocationHasParticularOperator(URLS.ksazain);
  const isBahrainZainSLAOperator =
    isBH(homeData?.country) && isBHZainSLA(subscribeData?.operatorName || operator?.value);
  const isLYCountry = isLY(homeData.country);
  const isResendOTPAvailable = !!subscribeData?.isResendOTP;

  const inputClassName = useMemo(() => {
    if (isV3Route) {
      return classNames(
        styles.otp_input,
        Object.keys(errors).length > 0 || alerts.length > 0 ? styles.otp_input_error : '',
        'mt-2'
      );
    }

    return classNames(
      uniqueApiSource && styles['pin-input-full-uaeUnique'],
      'pin-input-full',
      errors.pin && 'input-error'
    );
  }, [errors, isV3Route, uniqueApiSource, alerts]);

  const buttonClassName = useMemo(() => {
    if (isV3Route) return classNames(styles.subscribe_button, 'mt-4');

    return classNames('vuz-btn-1', 'large', 'lh-08', 'sub-btn-fix2', !uniqueApiSource && 'mb-4');
  }, [isV3Route, uniqueApiSource]);

  const buttonWrapperClassName = useMemo(() => {
    if (isV3Route) return classNames(styles.subscribe_button_wrapper, 'text-center pos-rel');
    return 'text-center pos-rel';
  }, [isV3Route]);

  const inputPlaceholder = Array(otpDigitsCount)
    .fill(isBahrainZainSLAOperator ? 'X' : '•')
    .join(' ');
  const tcStyles = classNames('tc-div', { [styles.tc_center]: isTVLayout });

  const confirmPinButtonMainDefaultText = homeData?.vSubButtonOTP_Mobile
    ? homeData?.vSubButtonOTP_Mobile
    : t(isV3Route || isAE(homeData?.country) ? 'subscribe' : 'confirm');

  const confirmPinButtonSecondaryDefaultText =
    homeData?.vSubButtonOTP_Mobile && homeData?.vSubButtonOTP_Mobile_Offer
      ? homeData?.vSubButtonOTP_Mobile_Offer
      : null;

  const confirmPinButtonMainOperatorText = operatorSelectedDetails?.vSubButtonOTP_Mobile
    ? operatorSelectedDetails?.vSubButtonOTP_Mobile
    : confirmPinButtonMainDefaultText;

  const confirmPinButtonSecondaryOperatorText =
    operatorSelectedDetails?.vSubButtonOTP_Mobile &&
    operatorSelectedDetails?.vSubButtonOTP_Mobile_Offer
      ? operatorSelectedDetails?.vSubButtonOTP_Mobile_Offer
      : confirmPinButtonSecondaryDefaultText;

  const formattedOTPButtonText =
    subscribeData?.otpButtonText ||
    otpButtonText?.[language?.locale] ||
    confirmPinButtonMainOperatorText;
  const formattedOTPButtonOfferText = subscribeData?.otpButtonText
    ? ''
    : otpButtonText?.[language?.locale]
    ? otpButtonOfferText?.[language?.locale]
    : confirmPinButtonSecondaryOperatorText;

  const configurationsTermsAndConditionsHTML =
    termsAndConditionsDCB?.[homeData?.country]?.[language.locale];
  const operatorConfigurationsTermsAndConditionsHTML =
    urlConfigOperatorTermsAndConditions?.[subscribeData?.operatorName || operator?.value]?.[
      language.locale
    ];

  const formattedTextAboveBanner = urlConfigTextAboveOTPBanner?.[language.locale] || '';
  const formattedTextUnderBanner = urlConfigTextUnderOTPBanner?.[language.locale] || '';
  const formattedTextUnderCTAButton = urlConfigtextUnderOTPButton?.[language.locale] || '';
  const bannerTextOTP = urlConfigTextOnOTPBanner?.[language.locale] || null;

  return (
    <>
      <MainLayoutContainer>
        <section
          className="phone-payment-section"
          style={uniqueApiSource ? { paddingTop: '72px' } : {}}
        >
          <DynamicScrollToTop />

          {homeData?.source === WEB_ETISALAT_IMMERSIVE ? (
            <div className="otp-banner-wrapper">
              <img
                className="otp-banner"
                src={otpBanners.otp_banner_etisalat_immersive}
                alt="etisalat immersive banner"
              />
            </div>
          ) : null}

          {Object.keys(urlConfigOtpBannerConfig).length !== 0 ? (
            <div className="otp-banner-wrapper">
              {formattedTextAboveBanner ? (
                <p className="text-under-otp-banner">{formattedTextAboveBanner}</p>
              ) : null}

              <div style={{ position: 'relative' }}>
                <BannerTop bannerConfig={urlConfigOtpBannerConfig} />
                {bannerTextOTP ? <p className="textOnBanner">{bannerTextOTP}</p> : null}
              </div>

              {formattedTextUnderBanner ? (
                <p className="text-under-otp-banner">{formattedTextUnderBanner}</p>
              ) : null}
            </div>
          ) : null}

          <form onSubmit={(e) => handleSubmit(e)} id="otp-pin-form">
            <Row>
              <Col xs={12}>
                {isV3Route ? (
                  <div className={styles.goBackBtnContainer}>
                    {isTVLayout ? null : (
                      <div className={styles.go_back} onClick={history.goBack}>
                        <GoBack />
                      </div>
                    )}
                    <div className={classNames(styles.title)}>
                      <span>{t('please_enter_otp_sent_to_your_mobile_number')}</span>
                      <span className={styles.mobile_number}>{phone}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {!uniqueApiSource ? (
                      <label className="vuz-label-1 verification-label"> {t('verification')}</label>
                    ) : null}
                    <p
                      className={classNames(
                        uniqueApiSource && 'enter-pin-code-uaeUnique',
                        'enter-pin-code'
                      )}
                    >
                      {isLYCountry ? t('otp_input_header_ly') : t('enter_pin')}
                    </p>
                  </>
                )}
              </Col>

              <Col xs={12}>
                <div className="text-center">
                  <div className="pin-input-divX text-center" style={{ position: 'relative' }}>
                    <input
                      className={inputClassName}
                      id={inputId}
                      type="tel"
                      minLength={minPinLength}
                      maxLength={maxPinLength}
                      onChange={(e) => handlePinChange(e)}
                      name="pin"
                      data-testid={TEST_IDS.otpInput}
                      autoComplete="new-password"
                      value={pin}
                      placeholder={inputPlaceholder}
                    />
                  </div>
                  {isV3Route ? (
                    <InputErrorMessage
                      className={styles.otpError}
                      error={errors.pin ? invalidPinV3Text : null}
                    />
                  ) : (
                    <p className="error-label-center mt-1">{errors.pin && invalidPinText}</p>
                  )}
                  {!isV3Route && isResendOTPAvailable ? (
                    <Timer
                      pin={pin}
                      antifraudUniqueId={antifraudUniqueId}
                      clickId={MAHESecondClickId}
                    />
                  ) : null}
                </div>
              </Col>
            </Row>

            {isV3Route ? (
              <InputErrorMessage
                className={styles.otpError}
                error={
                  alerts.length && (alerts[0].msg?.message || alerts[0].msg)
                    ? alerts[0].msg?.message || alerts[0].msg
                    : null
                }
                onDismiss={
                  alerts[0]?.dismissible &&
                  alerts[0]?.id &&
                  (alerts[0].msg?.message || alerts[0].msg)
                    ? () => dismissAlert(alerts[0].id)
                    : undefined
                }
              />
            ) : (
              <Alert />
            )}

            <Row className={classNames('mt-3', !uniqueApiSource && 'mb-4')}>
              <Col xs={12} className="pin-form_buttonPrice-wrapper">
                {isQA(homeData?.country) ? <div>{confirmationPrice}</div> : null}
                <div className={buttonWrapperClassName}>
                  <button
                    style={
                      uniqueApiSource
                        ? {
                            margin: '0 auto',
                            width: '236px',
                            height: '40px',
                            marginBottom: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }
                        : null
                    }
                    id="submit-otp"
                    data-testid={TEST_IDS.submitOtpButton}
                    className={buttonClassName}
                    disabled={btnLoading || invalidLength}
                  >
                    {!uniqueApiSource ? (
                      <div className={styles.confirmPinButton}>
                        <span className="subscribe-btn-txt">
                          {btnLoading ? <RoundLoader /> : formattedOTPButtonText}
                        </span>

                        {!btnLoading && formattedOTPButtonOfferText ? (
                          <span className={styles.pinFormOfferText}>
                            {formattedOTPButtonOfferText}
                          </span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="subscribe-btn-txt" style={{ fontSize: '16px' }}>
                        {t('subscribe_unique')}
                      </span>
                    )}
                  </button>

                  {isV3Route ? (
                    <div className={isResendOTPAvailable ? 'mt-3' : ''}>
                      {isResendOTPAvailable ? (
                        <Timer
                          pin={pin}
                          antifraudUniqueId={antifraudUniqueId}
                          clickId={MAHESecondClickId}
                        />
                      ) : null}
                    </div>
                  ) : formattedTextUnderCTAButton ? (
                    <p className="mt-3 fs-10">{formattedTextUnderCTAButton}</p>
                  ) : operatorConfigurationsTermsAndConditionsHTML ? (
                    <div className="mt-3 fs-10" dangerouslySetInnerHTML={{ __html: operatorConfigurationsTermsAndConditionsHTML }} />
                  ) : (
                    <PinFormOperatorText isBHZainSLAOperator={isBahrainZainSLAOperator} />
                  )}
                </div>
              </Col>
            </Row>

            {displayExitButton && !uniqueApiSource && !isV3Route ? (
              <div className="exit-div-outer">
                <div className="exit-div" onClick={handleExitPage}>
                  <img src={Icons.exit} className="exit-img" alt="" />
                  <span className="exit-span">{t('exit_page')}</span>
                </div>
              </div>
            ) : uniqueApiSource ? (
              <div className="exit-div-uaeUnique" onClick={handleExitPage}>
                {t('exit_unique')}
              </div>
            ) : null}

            {uniqueApiSource ? (
              <>
                <OperatorsMessage />
                <AfterClickingSubscribe message={'after_clicking_subscribe_will_activate'} />
              </>
            ) : null}

            {operator &&
            homeData &&
            !isTVLayout &&
            (isKSAZain ||
              isAE(homeData.country) ||
              isIQ(homeData.country) ||
              isQA(homeData.country) ||
              isBH(homeData.country)) ? (
              <div
                className={tcStyles}
                dangerouslySetInnerHTML={{
                  __html:
                    operatorCustomTerms ||
                    operatorConfigurationsTermsAndConditionsHTML ||
                    configurationsTermsAndConditionsHTML ||
                    homeData.fullTCText,
                }}
              />
            ) : null}
          </form>
          {!isV3Route ? (
            <Footer
              vipFeaturesConfig={{ hide: uniqueApiSource || isV3Route }}
              aboutUsConfig={{ onlyLinks: isV3Route }}
              isOTP={!otpError}
              isWhite={false}
            />
          ) : null}
        </section>
      </MainLayoutContainer>
      {!isTVLayout ? (
        <div className={styles.separatorLineContainer}>
          <LineSeparator className={styles.separatorLine} />
        </div>
      ) : null}
      {isV3Route ? (
        <div style={{ backgroundColor: '#0F1233', width: '100%' }}>
          <Footer
            vipFeaturesConfig={{ hide: uniqueApiSource || isV3Route }}
            aboutUsConfig={{ onlyLinks: isV3Route }}
            isOTP={!otpError}
            isWhite={false}
          />
        </div>
      ) : null}
    </>
  );
};

export { PinForm };
